import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';

const STORAGE_KEY = 'app-theme';

const DARK_TOKENS = {
  ink: '#0A0A0B',
  inkSolid: '#010409',
  panel: '#0F0F12',
  cream: '#F5F5F0',
  text: '#F5F5F0',
  muted: 'rgba(245,245,240,0.55)',
  faint: 'rgba(245,245,240,0.32)',
  hairline: 'rgba(245,245,240,0.07)',
  hairlineStrong: 'rgba(245,245,240,0.14)',
  navBgScrolled: 'rgba(10,10,11,0.9)',
  navBg: 'rgba(10,10,11,0.65)',
  accent: '#A3FF7C',
};

const LIGHT_TOKENS = {
  ink: '#F5F5F0',
  inkSolid: '#FFFFFF',
  panel: '#F0EFEA',
  cream: '#0A0A0B',
  text: '#0A0A0B',
  muted: 'rgba(10,10,11,0.6)',
  faint: 'rgba(10,10,11,0.4)',
  hairline: 'rgba(10,10,11,0.08)',
  hairlineStrong: 'rgba(10,10,11,0.18)',
  navBgScrolled: 'rgba(245,245,240,0.92)',
  navBg: 'rgba(245,245,240,0.7)',
  accent: '#3FB962',
};

const ThemeContext = createContext({
  theme: 'light',
  tokens: LIGHT_TOKENS,
  toggleTheme: () => {},
  setTheme: () => {},
});

const getInitialTheme = () => {
  if (typeof window === 'undefined') return 'light';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  return 'light';
};

const applyCssVariables = (theme) => {
  if (typeof document === 'undefined') return;
  const t = theme === 'dark' ? DARK_TOKENS : LIGHT_TOKENS;
  const root = document.documentElement;
  root.setAttribute('data-theme', theme);
  root.style.setProperty('--bg-ink', t.ink);
  root.style.setProperty('--bg-ink-solid', t.inkSolid);
  root.style.setProperty('--bg-panel', t.panel);
  root.style.setProperty('--color-text', t.text);
  root.style.setProperty('--color-cream', t.cream);
  root.style.setProperty('--color-muted', t.muted);
  root.style.setProperty('--color-faint', t.faint);
  root.style.setProperty('--hairline', t.hairline);
  root.style.setProperty('--hairline-strong', t.hairlineStrong);
  root.style.setProperty('--nav-bg', t.navBg);
  root.style.setProperty('--nav-bg-scrolled', t.navBgScrolled);
  root.style.setProperty('--accent', t.accent);
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(getInitialTheme);

  useEffect(() => {
    applyCssVariables(theme);
    try { window.localStorage.setItem(STORAGE_KEY, theme); } catch (_) {}
  }, [theme]);

  const setTheme = useCallback((next) => {
    setThemeState(next === 'light' ? 'light' : 'dark');
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState(t => (t === 'light' ? 'dark' : 'light'));
  }, []);

  const value = useMemo(() => ({
    theme,
    tokens: theme === 'dark' ? DARK_TOKENS : LIGHT_TOKENS,
    setTheme,
    toggleTheme,
  }), [theme, setTheme, toggleTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);
